import { Routes } from '@angular/router';
import { MyProceduresRouteNames } from '@pages/my-procedures/my-procedures-route-names';
import { authGuard } from '@core/guards/auth.guard';
import { MyProceduresDetailRouteNames } from '@pages/my-procedures/details/my-procedures-detail.routes';
import { MyAccountRouteNames } from '@pages/my-account/my-account.routes';
import { userIsGGCCGuard } from '@core/guards/user-is-ggcc.guard';
import { userIsAdminGuard } from '@core/guards/user-is-admin.guard';
import { UserManagementRouteNames } from '@pages/user-management/user-management.routes';
import { AlertsRouteNames } from './pages/alerts/alerts.routing';
import { MyConsumptionRouteNames } from './pages/my-consumption/my-consumption.routes';
import { MyInvoicesRoutesName } from '@pages/my-invoices/my-invoices.routes';
import { analyticsResolver } from '@core/resolvers/analytics.resolver';
import { ProceduresListingTab } from '@core/domain/model/procedures/procedures-listing.model';
import { MyServicesRouteNames } from '@pages/my-services/my-services.routes';
import { FAQRouteNames } from '@pages/frequently-asked-questions/frequently-asked-questions.routes';
import { MySustainabilityRouteNames } from '@pages/sustainability/sustainability.routes';

export enum AppRouteNames {
  ROOT = '/',
  HOME = 'home',
  SERVICES = 'my-services',
  INVOICES = 'my-invoices',
  MY_CONSUMPTION = 'my-consumption',
  PROCEDURES = 'my-procedures',
  SUSTAINABILITY = 'sustainability',
  MARKET = 'market',
  USER_MANAGEMENT = 'user-management',
  MY_ACCOUNT = 'account',
  NOT_FOUND = 'not-found',
  UNAUTHORIZED = 'unauthorized',
  NOTIFICATIONS = 'notifications',
  AZURE = 'azureAD',
  ONELOGIN = 'onelogin',
  ALERTS = 'alerts',
  FAQ = 'faq',
  COOKIES = 'cookies',
  REGISTER = 'register',
  PRIVACY = 'privacy',
  LISTING = 'listing',
  LEGAL = 'legal',
  CONTACT_INFORMATION = 'contact-information',
}

export type AllRouteNamesType =
  | AppRouteNames
  | MyProceduresRouteNames
  | MyProceduresDetailRouteNames
  | MyAccountRouteNames
  | UserManagementRouteNames
  | AlertsRouteNames
  | MyConsumptionRouteNames
  | MyInvoicesRoutesName
  | MyServicesRouteNames
  | ProceduresListingTab
  | FAQRouteNames
  | MySustainabilityRouteNames;

export const AllRouteNames = {
  ...AppRouteNames,
  ...MyProceduresRouteNames,
  ...MyInvoicesRoutesName,
  ...MyProceduresDetailRouteNames,
  ...MyAccountRouteNames,
  ...UserManagementRouteNames,
  ...AlertsRouteNames,
  ...MyConsumptionRouteNames,
  ...ProceduresListingTab,
  ...FAQRouteNames,
  ...MyServicesRouteNames,
  ...MySustainabilityRouteNames,
};
export const APP_ROUTES: Routes = [
  {
    path: '',
    data: { breadcrumb: 'ROUTES.INIT' },
    children: [
      {
        path: '',
        pathMatch: 'full',
        data: {
          breadcrumb: { skip: true },
        },
        redirectTo: AppRouteNames.HOME,
      },
      {
        path: AppRouteNames.HOME,
        loadComponent: () => import('@pages/home/home.page').then((p) => p.HomePage),
        data: {
          breadcrumb: { skip: true },
        },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.SERVICES,
        loadChildren: () => import('@pages/my-services/my-services.routes').then((p) => p.MY_SERVICES_ROUTES),
        data: {
          breadcrumb: 'ROUTES.SERVICES',
          analyticsParams: { screenName: 'My services' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.INVOICES,
        loadChildren: () => import('@pages/my-invoices/my-invoices.routes').then((p) => p.MY_INVOICES_ROUTES),
        data: {
          breadcrumb: 'ROUTES.INVOICES',
          analyticsParams: { screenName: 'My invoices' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.MY_CONSUMPTION,
        loadChildren: () => import('@pages/my-consumption/my-consumption.routes').then((p) => p.MY_CONSUMPTION_ROUTES),
        data: {
          breadcrumb: 'ROUTES.MY_CONSUMPTION',
          analyticsParams: { screenName: 'My consumption' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.PROCEDURES,
        loadChildren: () => import('@pages/my-procedures/my-procedures.routes').then((r) => r.MY_PROCEDURES_ROUTES),
        data: {
          breadcrumb: 'ROUTES.PROCEDURES',
          analyticsParams: { screenName: 'My procedures' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.SUSTAINABILITY,
        loadChildren: () => import('@pages/sustainability/sustainability.routes').then((p) => p.MY_SUSTAINABILITY_ROUTES),
        data: {
          breadcrumb: 'ROUTES.SUSTAINABILITY',
          analyticsParams: { screenName: 'Sustainability' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard, userIsGGCCGuard],
      },
      {
        path: AppRouteNames.MARKET,
        loadComponent: () => import('@pages/market/market.page').then((p) => p.MarketPage),
        data: {
          breadcrumb: 'ROUTES.MARKET',
          analyticsParams: { screenName: 'Market' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard, userIsGGCCGuard],
      },
      {
        path: AppRouteNames.USER_MANAGEMENT,
        loadChildren: () => import('@pages/user-management/user-management.routes').then((p) => p.USER_MANAGEMENT_ROUTES),
        data: {
          breadcrumb: 'ROUTES.USER_MANAGEMENT',
          analyticsParams: { screenName: 'User management' },
        },
        resolve: { analyticsResolver: analyticsResolver },
        canMatch: [authGuard, userIsAdminGuard],
      },
      {
        path: AppRouteNames.MY_ACCOUNT,
        loadChildren: () => import('@pages/my-account/my-account.routes').then((p) => p.MY_ACCOUNT_ROUTES),
        data: {
          breadcrumb: 'ROUTES.MY_ACCOUNT',
        },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.NOTIFICATIONS,
        loadChildren: () => import('@pages/notifications/notifications.routes').then((p) => p.NOTIFICATIONS_ROUTES),
        data: {
          breadcrumb: 'ROUTES.NOTIFICATIONS',
        },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.ALERTS,
        loadChildren: () => import('@pages/alerts/alerts.routing').then((p) => p.ALERTS_ROUTES),
        data: {
          breadcrumb: 'ROUTES.ALERTS',
        },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.FAQ,
        loadChildren: () => import('@pages/frequently-asked-questions/frequently-asked-questions.routes').then((p) => p.FAQ_ROUTES),
        data: {
          breadcrumb: 'FAQ.TITLE',
        },
        canMatch: [authGuard],
      },
      {
        path: AppRouteNames.COOKIES,
        loadComponent: () => import('@pages/cookies/cookies.page').then((p) => p.CookiesPage),
        data: {
          breadcrumb: 'ROUTES.COOKIES',
        },
      },
      {
        path: AppRouteNames.PRIVACY,
        loadComponent: () => import('@pages/privacy-policy/privacy.page').then((p) => p.PrivacyPage),
        data: {
          breadcrumb: 'ROUTES.PRIVACY',
        },
      },
      {
        path: AppRouteNames.LEGAL,
        loadComponent: () => import('@pages/legal-advice/legal-advice.page').then((p) => p.LegalAdvicePage),
        data: {
          breadcrumb: 'ROUTES.LEGAL',
        },
      },
    ],
  },
  {
    path: AppRouteNames.AZURE,
    loadComponent: () => import('@pages/azure-login/azure-login.page').then((p) => p.AzureLoginPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: AppRouteNames.ONELOGIN,
    loadComponent: () => import('@pages/onelogin-login/onelogin-login.page').then((p) => p.OneloginLoginPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: AppRouteNames.UNAUTHORIZED,
    loadComponent: () => import('@pages/unauthorized/unauthorized.page').then((p) => p.UnauthorizedPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: AppRouteNames.NOT_FOUND,
    loadComponent: () => import('@pages/not-found/not-found.page').then((p) => p.NotFoundPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: AppRouteNames.REGISTER,
    loadComponent: () => import('@pages/register/register.page').then((p) => p.RegisterPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: AppRouteNames.CONTACT_INFORMATION,
    loadComponent: () => import('@pages/contact-information/contact-information.page').then((p) => p.ContactInformationPage),
    data: {
      breadcrumb: { skip: true },
    },
  },
  {
    path: '**',
    redirectTo: AppRouteNames.NOT_FOUND,
    pathMatch: 'full',
  },
];
